import { Button, cn, Input, Label } from '@up2stay/up2promo-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useDialogManager } from '@up2promo-setup-frontend/core/hooks/use-dialog-manager';
import ButtonWithEnter from '../components/button-with-enter';
import { SetupStepShell } from '../components/setup-step-shell';
import { useIntercom } from '@up2promo-setup-frontend/core/hooks/use-intercom';
import { useSetupStore } from '@up2promo-setup-frontend/core/store/setup.store';
import { fadeInUp } from '@up2promo-setup-frontend/ui/animations/fade-in-up.animation';
import MessageModal from '@up2promo-setup-frontend/ui/components/modals/message.modal';
import {
  useDictionary,
  useLocale,
} from '@up2promo-setup-frontend/ui/providers/dictionary.provider';
import { ProductsPublicApi } from '@up2stay/up2promo-api-client';
import { motion } from 'motion/react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { usesUrlSetupFlow } from '@up2promo-setup-frontend/core/constants/setup-product-types';
import { ProductType } from '@up2promo-setup-frontend/core/types/product-type.enum';
import { buildSetupProductBody } from '@up2promo-setup-frontend/core/utils/product-type-api';
import { useSetupNavigation } from './hooks/use-setup-navigation';
import { Navigate, useParams } from 'react-router-dom';

export default function CustomUrlPage() {
  const translations = useDictionary();
  const locale = useLocale();
  const { goNext, navigateTo } = useSetupNavigation();
  const { hash } = useParams<{ hash: string }>();

  // Use selectors to only subscribe to needed values - ALL hooks must be called before any early returns
  const email = useSetupStore((state) => state.email);
  const type = useSetupStore((state) => state.type);
  const targetLink = useSetupStore((state) => state.targetLink);
  const setTargetLink = useSetupStore((state) => state.setTargetLink);
  const previousType = useSetupStore((state) => state.type);
  const suggestions = useSetupStore((state) => state.suggestions);

  const { openDialog } = useDialogManager();

  const intercom = useIntercom({
    user: hash ? { hash, email } : email ? { email } : undefined,
    enabled: true,
  });

  const [isLoading, setLoading] = useState(false);

  const urlSchema = z.object({
    url: z.string().min(1, translations.pages.customUrlStep.invalidUrl),
  });
  type CustomUrlValues = z.infer<typeof urlSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CustomUrlValues>({
    mode: 'onTouched',
    criteriaMode: 'all',
    resolver: zodResolver(urlSchema as never),
    defaultValues: {
      url: targetLink || '',
    },
  });

  const onSubmit = async (data: CustomUrlValues) => {
    try {
      setLoading(true);
      let targetUrl = data.url.trim();
      const originalUrl = targetUrl;
      let urlWasFixed = false;

      // Auto-fix common URL mistakes
      // If no protocol, add https://
      if (!targetUrl.match(/^https?:\/\//i)) {
        targetUrl = `https://${targetUrl}`;
        urlWasFixed = true;
      }

      // Validate the fixed URL
      let urlDomain: string | undefined;
      try {
        const validatedUrl = new URL(targetUrl);
        targetUrl = validatedUrl.href; // Use the canonicalized URL
        urlDomain = validatedUrl.hostname;
      } catch {
        // Track validation error
        if (intercom.isBooted()) {
          intercom.trackEvent('custom-url-validation-error', {
            hash,
            product_type: type,
            original_url:
              originalUrl.length > 100
                ? originalUrl.substring(0, 100) + '...'
                : originalUrl,
            url_was_fixed: urlWasFixed,
            has_email: !!email,
          });
        }

        void openDialog(MessageModal, {
          componentProps: {
            title: translations.pages.suggestions.error.title,
            bodyText: translations.pages.customUrlStep.invalidUrl,
            isDestructive: true,
          },
        });
        setLoading(false);
        return;
      }

      // Track custom URL submission
      if (intercom.isBooted()) {
        intercom.trackEvent('custom-url-submitted', {
          hash,
          product_type: type,
          url_domain: urlDomain,
          url_length: targetUrl.length,
          url_preview:
            targetUrl.length > 200
              ? targetUrl.substring(0, 200) + '...'
              : targetUrl,
          url_was_fixed: urlWasFixed,
          original_url_length: originalUrl.length,
          has_email: !!email,
        });
      }

      // Public product setup (no workspace); the contact gets the reset mail
      await ProductsPublicApi.setupProduct({
        body: buildSetupProductBody({
          hash: hash!,
          url: targetUrl,
          type,
          email,
          locale,
        }),
      });

      setTargetLink(targetUrl);
      goNext('custom-url');
    } catch {
      // Track creation error
      if (intercom.isBooted()) {
        intercom.trackEvent('custom-url-error', {
          hash,
          product_type: type,
          error_type: 'link_mapping_creation_failed',
          has_email: !!email,
        });
      }

      void openDialog(MessageModal, {
        componentProps: {
          title: translations.pages.suggestions.error.title,
          bodyText: translations.pages.suggestions.error.description,
          isDestructive: true,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  // Guard: Redirect if already in suggestion flow (has suggestions) and not custom type
  // MUST be after all hooks to maintain hook order
  if (suggestions && suggestions.length > 0 && !usesUrlSetupFlow(type)) {
    const basePath = hash ? `/${hash}` : '';
    return <Navigate to={`${basePath}/select-suggestion`} replace />;
  }

  // Check if we can show switch back (only if we came from a non-custom flow)
  const canSwitchBack = previousType && !usesUrlSetupFlow(previousType);

  return (
    <SetupStepShell>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-2 sm:gap-3"
      >
        <motion.h1
          custom={0}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="text-2xl font-semibold tracking-tight text-foreground text-balance md:text-3xl lg:text-4xl"
        >
          {translations.pages.customUrlStep.title}
        </motion.h1>
        <motion.p
          custom={1}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="max-w-prose text-sm text-foreground/70 text-pretty md:text-base"
        >
          {translations.pages.customUrlStep.description}
        </motion.p>

        <motion.div
          custom={2}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="my-4 flex flex-col gap-2 sm:my-6"
        >
          <Label htmlFor="setup-url">
            {translations.pages.customUrlStep.inputTitle}
          </Label>
          <div className="flex flex-col gap-1">
            <Input
              id="setup-url"
              autoFocus
              {...register('url')}
              placeholder={translations.pages.customUrlStep.inputPlaceholder}
              aria-invalid={!!errors.url}
              className={cn(errors.url && 'border-destructive')}
              // Defensive attributes to prevent browser/extension DOM mutations
              // that cause React removeChild crashes on route transition
              autoComplete="url"
              type="text"
              inputMode="url"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              data-lpignore="true"
              data-form-type="other"
            />
            {errors.url?.message && (
              <p className="text-sm text-destructive">{errors.url.message}</p>
            )}
          </div>
        </motion.div>

        <motion.div
          custom={3}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
        >
          <ButtonWithEnter
            type="submit"
            text={translations.pages.customUrlStep.button}
            isLoading={isLoading}
          />
        </motion.div>

        {canSwitchBack && (
          <motion.div
            custom={4}
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="mt-4 sm:mt-6"
          >
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                if (intercom.isBooted()) {
                  intercom.trackEvent('switched-back-from-custom', {
                    hash,
                    from_type: ProductType.CUSTOM,
                    has_email: !!email,
                  });
                }
                navigateTo.input();
              }}
            >
              {translations.pages.customUrlStep.switchBack}
            </Button>
          </motion.div>
        )}
      </form>
    </SetupStepShell>
  );
}
